export default function SolarCalculator() {
  return (
    <section id="solar-calculator" className="relative py-20 md:py-28 px-4 md:px-6 lg:px-10 bg-background-50 overflow-hidden">
      {/* Subtle background decoration */}
      <div className="absolute inset-0 pointer-events-none">
        <div
          className="absolute top-0 left-0 right-0 h-px"
          style={{ background: 'linear-gradient(to right, transparent, #2FA84F30, transparent)' }}
        />
        <div
          className="absolute -top-32 right-0 w-[500px] h-[500px] rounded-full opacity-[0.04]"
          style={{ background: 'radial-gradient(circle, #2FA84F, transparent 70%)' }}
        />
        <div
          className="absolute -bottom-32 left-0 w-[400px] h-[400px] rounded-full opacity-[0.04]"
          style={{ background: 'radial-gradient(circle, #F4D13D, transparent 70%)' }}
        />
      </div>

      <div className="relative max-w-7xl mx-auto">
        {/* Section header */}
        <div className="text-center mb-10">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-primary-50 border border-primary-200/50 mb-5">
            <div className="w-2 h-2 rounded-full bg-primary-500 animate-pulse" />
            <span className="text-xs font-semibold text-primary-700 tracking-wide uppercase whitespace-nowrap">Free Tool</span>
          </div>
          <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-extrabold tracking-tight leading-tight text-foreground-950 mb-4">
            Australian Solar Calculator
          </h2>
          <p className="text-base md:text-lg text-foreground-600 max-w-2xl mx-auto leading-relaxed">
            Estimate your solar savings, system size, battery requirements and return on investment — for residential, commercial and industrial customers across Australia.
          </p>
        </div>

        {/* Stats row */}
        <div className="flex flex-wrap justify-center gap-8 mb-10">
          {[
            { value: '2.9M+', label: 'Australian solar homes' },
            { value: '30%+', label: 'Average bill reduction' },
            { value: '25 yr', label: 'System lifetime modelled' },
            { value: '100%', label: 'Renewable focused' },
          ].map((stat) => (
            <div key={stat.label} className="text-center">
              <p className="text-xl md:text-2xl font-extrabold text-primary-600 font-heading">{stat.value}</p>
              <p className="text-xs text-foreground-500 font-medium mt-0.5">{stat.label}</p>
            </div>
          ))}
        </div>

        {/* Calculator preview image — click to open */}
        <a
          href="https://akenergy-solar-calculator.base44.app"
          target="_blank"
          rel="noopener noreferrer"
          className="block relative rounded-2xl overflow-hidden border border-background-200/70 group cursor-pointer"
          aria-label="Open AK Energy Solar Calculator"
        >
          <img
            src="https://readdy.ai/api/search-image?query=Modern%20Australian%20house%20with%20rooftop%20solar%20panels%20and%20electric%20vehicle%20in%20driveway%2C%20bright%20sunny%20day%2C%20clean%20suburban%20neighbourhood%2C%20green%20lawn%2C%20wide%20angle%2C%20photorealistic%2C%20crisp%20blue%20sky%2C%20warm%20natural%20light%2C%20professional%20real%20estate%20photography%20style%2C%20fresh%20and%20inviting%20with%20simple%20light%20background&width=1400&height=800&seq=solar-calc-preview-01&orientation=landscape"
            alt="AK Energy Solar Calculator — estimate solar savings for Australian homes and businesses"
            className="w-full object-cover object-top transition-transform duration-500 group-hover:scale-[1.02]"
            style={{ height: '420px' }}
          />
          {/* Overlay gradient */}
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/10 to-transparent" />
          {/* Overlay text */}
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-4 px-6 text-center">
            <div className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-2xl px-8 py-6 flex flex-col items-center gap-4 max-w-md">
              <div className="w-12 h-12 flex items-center justify-center rounded-full bg-primary-500">
                <i className="ri-sun-line text-white text-2xl" />
              </div>
              <p className="text-white font-heading text-xl md:text-2xl font-bold leading-tight">
                Try the AK Energy Solar Calculator
              </p>
              <p className="text-white/80 text-sm leading-relaxed">
                Calculate your savings, system size &amp; ROI — residential, commercial &amp; industrial.
              </p>
              <span className="whitespace-nowrap inline-flex items-center gap-2 bg-primary-500 hover:bg-primary-600 text-white text-sm font-semibold px-6 py-2.5 rounded-full transition-colors duration-200">
                <i className="ri-calculator-line" />
                Start Calculating
              </span>
            </div>
          </div>
        </a>

        {/* Open in new tab link */}
        <div className="flex justify-center mt-5">
          <a
            href="https://akenergy-solar-calculator.base44.app"
            target="_blank"
            rel="noopener noreferrer"
            className="whitespace-nowrap inline-flex items-center gap-2 px-5 py-2.5 text-sm font-medium rounded-lg border border-background-200 text-foreground-600 hover:text-foreground-950 hover:border-foreground-300 hover:bg-background-100 transition-all duration-200 cursor-pointer"
          >
            <i className="ri-external-link-line"></i>
            Open full calculator in new tab
          </a>
        </div>
      </div>
    </section>
  );
}